import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";

import { SPEAK_EASY_CATEGORIES } from "../../../functions/speakEasyCategories";
import QuestionModal from "./QuestionModal";
import {
  QUESTION_PAGE_SIZES,
  downloadSampleCsv,
  exportQuestionsCsv,
  parseQuestionsCsv,
} from "./questionHelpers";

const ALL = "All";

function truncate(text, max = 80) {
  const s = String(text ?? "");
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Question editor from old/admin/editor.php + editorx.php (category filter buttons). */
export default function QuestionsTab({
  questions,
  isLoading,
  isSaving,
  onSaveQuestion,
  onDeleteQuestion,
  onImportCsv,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [category, setCategory] = useState(ALL);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const openAdd = () => {
    setErrorMessage("");
    setModalMode("add");
    setEditingQuestion(null);
    setModalOpen(true);
  };

  const openEdit = (q) => {
    setErrorMessage("");
    setModalMode("edit");
    setEditingQuestion(q);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingQuestion(null);
  };

  const handleModalSubmit = async (question) => {
    await onSaveQuestion({
      action: modalMode,
      editId: modalMode === "edit" ? editingQuestion?.id : null,
      question,
    });
    closeModal();
  };

  const handleDelete = async (q) => {
    const result = await Swal.fire({
      title: "Are you sure you want to delete this item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });
    if (!result.isConfirmed) return;
    await onDeleteQuestion(q.id);
  };

  const showCsvUploadError = (message) => {
    setSuccessMessage("");
    setErrorMessage(message);
    Swal.fire({ icon: "error", title: "CSV upload failed", text: message });
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!/\.csv$/i.test(file.name)) {
      showCsvUploadError("Please upload a .csv file.");
      return;
    }

    let rows;
    try {
      rows = parseQuestionsCsv(await file.text());
    } catch (err) {
      showCsvUploadError(err?.message || "Failed to parse CSV.");
      return;
    }

    const confirm = await Swal.fire({
      title: "Upload CSV",
      text: `This will overwrite all existing questions with ${rows.length} question(s) from the file.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Upload",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    setErrorMessage("");
    try {
      await onImportCsv(rows);
      setCategory(ALL);
      setPage(1);
    } catch {
      /* parent surfaces error */
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return questions.filter(
      (row) =>
        (category === ALL || row.category === category) &&
        (!q ||
          row.question_name?.toLowerCase().includes(q) ||
          row.category?.toLowerCase().includes(q))
    );
  }, [questions, category, search]);

  const countByCategory = useMemo(() => {
    const counts = {};
    questions.forEach((q) => {
      counts[q.category] = (counts[q.category] || 0) + 1;
    });
    return counts;
  }, [questions]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="themeupdate-panel-wide quiz-panel">
      <div className="quiz-page-header">
        <h2 className="quiz-page-title">Add Questions</h2>
        <div className="quiz-toolbar">
          <button type="button" className="quiz-toolbar-btn" onClick={openAdd} disabled={isSaving}>
            <i className="fa-solid fa-plus" aria-hidden /> Add Question
          </button>
          <label className="quiz-toolbar-btn quiz-upload-label">
            <i className="fa-solid fa-upload" aria-hidden /> Upload CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="quiz-upload-input"
              onChange={handleCsvUpload}
              disabled={isSaving}
            />
          </label>
          <button
            type="button"
            className="quiz-toolbar-btn"
            onClick={() => exportQuestionsCsv(questions)}
            disabled={!questions.length}
          >
            <i className="fa-regular fa-file-lines" aria-hidden /> Export CSV
          </button>
          <button type="button" className="quiz-toolbar-btn" onClick={downloadSampleCsv}>
            <i className="fa-solid fa-download" aria-hidden /> Sample CSV
          </button>
        </div>
      </div>

      <p className="qa-char-limit-hint">
        * Uploading a CSV overwrites all existing questions. Use only CSV files with the columns
        question_name, category (see the sample CSV). Whitespace is removed automatically.
      </p>

      <div className="se-category-filter" role="group" aria-label="Filter by category">
        {[ALL, ...SPEAK_EASY_CATEGORIES].map((c) => (
          <button
            key={c}
            type="button"
            className={`se-category-btn${category === c ? " active" : ""}`}
            onClick={() => {
              setCategory(c);
              setPage(1);
            }}
          >
            {c}
            <span className="se-category-count">
              {c === ALL ? questions.length : countByCategory[c] || 0}
            </span>
          </button>
        ))}
      </div>

      {(errorMessage || successMessage) && (
        <div className="qa-alerts">
          {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
          {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
        </div>
      )}

      <div className="quiz-table-card themeupdate-card">
        <div className="quiz-table-controls">
          <label className="quiz-table-show">
            Show{" "}
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {QUESTION_PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>{" "}
            entries
          </label>
          <label className="quiz-table-search">
            Search:{" "}
            <input
              type="search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>

        <div className="quiz-table-wrap">
          <table className="quiz-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Question Name</th>
                <th>Category</th>
                <th>Edit</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="quiz-table-empty">
                    Loading questions…
                  </td>
                </tr>
              ) : pageRows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="quiz-table-empty">
                    No questions yet. Click <strong>Add Question</strong> or upload a CSV.
                  </td>
                </tr>
              ) : (
                pageRows.map((q, idx) => (
                  <tr key={q.id}>
                    <td>{(safePage - 1) * pageSize + idx + 1}</td>
                    <td className="quiz-cell-question" title={q.question_name}>
                      {truncate(q.question_name, 80)}
                    </td>
                    <td>{q.category || "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="quiz-table-btn quiz-table-btn-edit"
                        onClick={() => openEdit(q)}
                      >
                        <i className="fa-solid fa-pencil" aria-hidden /> Edit
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="quiz-table-btn quiz-table-btn-delete"
                        onClick={() => handleDelete(q)}
                        disabled={isSaving}
                      >
                        <i className="fa-solid fa-trash" aria-hidden /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="quiz-table-footer">
          <span className="quiz-table-info">
            Showing {filtered.length ? (safePage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(safePage * pageSize, filtered.length)} of {filtered.length} entries
            {filtered.length !== questions.length ? ` (filtered from ${questions.length})` : ""}
          </span>
          <div className="quiz-table-pager">
            <button
              type="button"
              className="quiz-pager-btn"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span className="quiz-pager-num">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              className="quiz-pager-btn"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <QuestionModal
        open={modalOpen}
        mode={modalMode}
        initial={editingQuestion}
        defaultCategory={category === ALL ? "" : category}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isSaving={isSaving}
      />
    </div>
  );
}
